/**
 * 메인 대시보드 "PQCD 변화율 게이지" + 상단 KPI 카드 5개의 계산 로직.
 *
 * Phase 4(2026-10, 사용자 승인) 부터 화면 상단 "기준 연도" 셀렉터 자체를 없앴다 - 비교 기준
 * (base)을 사람이 고르는 게 아니라 "항목마다" 자동으로 정한다:
 *
 *   작년(올해-1년) 1~12월 중 데이터 있는 달이 하나라도 있으면 → 'lastYear' 모드 :
 *     base = 작년 중 데이터 있는 달들의 단순평균.
 *   작년에 데이터가 전혀 없으면(신규 지표 등) → 'recent3' 모드 :
 *     base = 지난달로 끝나는 3개월(getBaseMonths) 중 데이터 있는 달들의 단순평균.
 *
 * 카드/게이지마다(심지어 같은 화면 안에서도) 모드가 다를 수 있다 - 예를 들어 "재공재고"는
 * 작년에 스냅샷이 없어 recent3 로 떨어지는데 "생산완료수량"은 작년 실적이 있어 lastYear 로
 * 뜰 수 있다. 그래서 chooseBaseline 은 월별 값 맵(monthlyMap) 하나를 받아 그 항목 기준으로만
 * 모드를 정한다 - 화면 전체가 공유하는 설정값이 아니다. 화면(MainDashboardPage 등)은 각
 * calc 함수가 돌려주는 baseMode 를 그 항목의 sub-line 라벨에 그대로 쓴다('작년 월평균' vs
 * '3개월 월평균').
 *
 * current(이번달, 1일~오늘)는 모드와 무관하게 항상 고정이다 - base 만 항목별로 자동으로
 * 바뀐다.
 *
 * base 기간이 몇 개월이든 "base = base 기간에 속한 달의 월별 값 단순평균" 규칙은 같다 -
 * 12개월치든 3개월치든 한 번에 합쳐서 비율을 내지 않고, 달마다 값을 먼저 낸 뒤 그 값들을
 * 평균한다 - 달마다 표본 크기(작업시간·검사건수 등)가 다르면 두 방식 결과가 갈린다. 데이터가
 * 아예 없는 달(분모 0 또는 행 자체가 없음)은 0으로 치지 않고 평균에서 통째로 뺀다 - 0으로
 * 치면 "그 달에 실적이 0건" 과 "그 달 평균이 0" 이 구분되지 않는다. 몇 개 달로 평균을 냈는지
 * (baseMonthCount)를 항상 같이 돌려줘서 화면이 "N개월 평균" 이라고 밝힐 수 있게 한다.
 *
 * 화면 표시(반올림, 단위, '-'/그레이 대체 텍스트)는 이 모듈이 하지 않는다 - 순수 계산만 두고
 * 컴포넌트/페이지가 그 값을 어떻게 보여줄지 정한다. 그래야 이 파일만 따로 테스트할 수 있다
 * (pqcdTrend.test.js).
 *
 * ⚠ C(재공재고)만 계산 방식이 다르다(Phase 2, 2026-09 - "납품창고 입고 = 납품완료"로 기준을
 * 바꾸면서 KPI_WIP_INF 를 안 쓰고 asOfDt 시점 스냅샷(selectWipSnapshot)으로 다시 정의했다).
 * p/q/d 는 월별로 쌓인 실적 목록을 여기서 합산해 "월별 값"을 만들지만, c 는 서버가 이미
 * 그 시점(base 월이면 그 달 "말일", current 면 "오늘")의 재공수량을 계산해서 한 건씩 준다 -
 * 그래서 입력도 월별 실적 목록이 아니라 "월(kpiYm)별 스냅샷 한 건짜리 목록"이다(calcWipTrend
 * 참고). lastYear 모드에서는 스냅샷을 12번(월말) 요청해야 하고, recent3 모드에서는 3번이
 * 필요하다 - 호출부(usePqcdData)는 어느 모드가 될지 미리 알 수 없으니 둘 다(+오늘) 요청해
 * 두고 chooseBaseline 이 사후에 고른다.
 *
 * Phase 4 에서 새로 추가된 calcItemScore 는 메인 대시보드·KPI 리포트의 "PQCD 스코어" 레이더
 * 전용이다 - base=100점을 기준으로 현재 값이 몇 점인지 낸다(낮을수록 좋은 지표는 분자/분모를
 * 뒤집는다). 화면이 그 값으로 레이더를 그린다.
 */

const YM_LENGTH = 6; // 'YYYYMM'

/** year, 1~12 월을 받아 'YYYYMM' 문자열로 합친다. */
const formatYm = (year, month) => `${year}${String(month).padStart(2, '0')}`;

/**
 * (year, month) 에서 delta개월만큼 이동한 (year, month) 를 낸다. delta는 음수/양수 모두
 * 허용한다. 자바스크립트의 %는 음수에서 음수를 낼 수 있어(-2 % 12 === -2) 그대로 쓰면
 * 1월 이전으로 넘어갈 때 month가 음수가 된다 - 그래서 12를 한 번 더 더하고 다시 나눈다.
 */
const shiftYearMonth = (year, month, delta) => {
  const zeroBased = month - 1 + delta;
  const y = year + Math.floor(zeroBased / 12);
  const m = ((zeroBased % 12) + 12) % 12;
  return { year: y, month: m + 1 };
};

/** 오늘(today) 이 속한 달을 'YYYYMM' 으로 낸다. 이번달(current) 구간의 기준값. */
export const getCurrentYm = (today) => formatYm(today.getFullYear(), today.getMonth() + 1);

/**
 * base(기준) 3개월의 'YYYYMM' 목록을 과거→최근 순으로 낸다. "지난달로 끝나는 3개월" 이라
 * 오늘이 속한 달(이번달)은 절대 포함하지 않는다. 작년에 데이터가 전혀 없을 때(chooseBaseline
 * 참고) recent3 모드의 base 로 쓰인다.
 *
 * 연도 경계를 shiftYearMonth 에 위임했기 때문에 1~3월에 호출해도(작년으로 넘어가도)
 * 그냥 동작한다 - 예: 2027-02 → ['202611', '202612', '202701'].
 */
export const getBaseMonths = (today) => {
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  const months = [];
  for (let i = 3; i >= 1; i -= 1) {
    const { year, month } = shiftYearMonth(y, m, -i);
    months.push(formatYm(year, month));
  }
  return months;
};

/**
 * 작년(today.getFullYear() - 1) 1~12월의 'YYYYMM' 목록을 낸다. chooseBaseline 의 1차 후보
 * (lastYear 모드) - "작년에 데이터가 있으면 작년 월평균과 비교한다"는 원칙의 기준 월 목록이다.
 */
export const getLastYearMonths = (today) => {
  const y = today.getFullYear() - 1;
  const months = [];
  for (let m = 1; m <= 12; m += 1) {
    months.push(formatYm(y, m));
  }
  return months;
};

/**
 * 'YYYYMM' 월의 마지막 날짜를 'YYYY-MM-DD' 로 낸다. 재공재고 스냅샷(C)의 base 월은
 * "그 달 말일 기준" 으로 찍는다 - 월중 아무 날짜나 고르면 그날그날 들쭉날쭉한 재공수량
 * 때문에 그 달을 대표하는 값이라 보기 어렵다. new Date(year, month, 0) 은 "그 달의 0일째"
 * 즉 전달 마지막 날을 주는 자바스크립트 관용구라, 윤년 2월(28/29일) 처리를 손으로 안 해도 된다.
 */
export const getMonthEndDate = (ym) => {
  const year = Number(ym.slice(0, 4));
  const month = Number(ym.slice(4, 6));
  const lastDay = new Date(year, month, 0).getDate();
  return `${ym.slice(0, 4)}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
};

/** Date → 'YYYY-MM-DD'. 재공재고 "이번달(오늘)" 스냅샷 요청에 쓴다(월말이 아니라 오늘). */
export const formatDateYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * 목록을 ymField 값으로 묶어 numField/denField 합계의 비율(× multiplier)을 낸다.
 * 분모 합계가 0(또는 그 달에 행 자체가 없음)이면 그 달은 맵에 아예 넣지 않는다 -
 * "데이터 없음" 과 "값이 0" 을 구분해야 평균에서 자연스럽게 빠지기 때문이다
 * (KpiReportService.calcCostSavingRate 와 같은 방식).
 */
const buildMonthlyRatio = (list, { ymField, numField, denField, multiplier = 1 }) => {
  const sums = {};
  (list || []).forEach((row) => {
    const ym = row[ymField];
    if (!ym || String(ym).length !== YM_LENGTH) return;
    if (!sums[ym]) sums[ym] = { num: 0, den: 0 };
    sums[ym].num += Number(row[numField]) || 0;
    sums[ym].den += Number(row[denField]) || 0;
  });
  const result = {};
  Object.keys(sums).forEach((ym) => {
    if (sums[ym].den > 0) {
      result[ym] = { value: (sums[ym].num / sums[ym].den) * multiplier, count: sums[ym].den };
    }
  });
  return result;
};

/**
 * 목록을 ymField 값으로 묶어 valField 합계를 낸다(비율이 아니라 단순 합 - 상단 KPI 카드
 * "생산완료수량" 처럼 "그 달에 얼마나 났나"를 그대로 더할 때 쓴다). 그 달에 행이 하나도
 * 없으면 맵에 넣지 않는다("실적 0건"과 "행 자체가 없다" 를 구분 - averageBaseMonths 가
 * 이 맵에 없는 달을 자동으로 평균에서 뺀다).
 */
const buildMonthlySum = (list, { ymField, valField }) => {
  const sums = {};
  const rowCounts = {};
  (list || []).forEach((row) => {
    const ym = row[ymField];
    if (!ym || String(ym).length !== YM_LENGTH) return;
    sums[ym] = (sums[ym] || 0) + (Number(row[valField]) || 0);
    rowCounts[ym] = (rowCounts[ym] || 0) + 1;
  });
  const result = {};
  Object.keys(sums).forEach((ym) => {
    if (rowCounts[ym] > 0) {
      result[ym] = { value: sums[ym], count: rowCounts[ym] };
    }
  });
  return result;
};

/**
 * 이미 월별로 한 행씩 집계돼서 오는 목록(계약~출하 리드타임 API, 재공재고 스냅샷처럼 서버가
 * 이미 값을 하나로 낸 경우)을 ym → {value, count} 맵으로 바꾼다. valField 가 null 인 행은
 * "데이터 없음"으로 보고 뺀다 - 0 은 null 이 아니므로(0 == null 은 false) 그대로 남는다.
 * 이 "0 은 살리고 null 만 뺀다"가 재공재고에서 특히 중요하다 - 아래 calcWipTrend 참고.
 */
const buildMonthlyDirect = (list, { ymField, valField, countField }) => {
  const result = {};
  (list || []).forEach((row) => {
    const ym = row[ymField];
    if (!ym || String(ym).length !== YM_LENGTH) return;
    const value = row[valField];
    if (value == null) return;
    result[ym] = { value: Number(value), count: Number(row[countField]) || 0 };
  });
  return result;
};

/**
 * baseMonths 중 monthlyMap 에 값이 있는 달만 골라 단순평균을 낸다.
 * 데이터가 하나도 없으면(0개월) base 는 null - "평균이 0" 이 아니라 "잴 수 없다" 이므로
 * 0으로 채우면 안 된다(게이지 분모가 0이 되어 % 가 무한대로 튀는 것도 막는다).
 */
const averageBaseMonths = (monthlyMap, baseMonths) => {
  const values = baseMonths
    .map((ym) => monthlyMap[ym])
    .filter((entry) => entry != null)
    .map((entry) => entry.value);
  if (values.length === 0) {
    return { base: null, baseMonthCount: 0 };
  }
  const avg = values.reduce((s, v) => s + v, 0) / values.length;
  return { base: avg, baseMonthCount: values.length };
};

/**
 * Phase 4(2026-10) 비교 기준(base) 자동 선택 - 항목(monthlyMap) 마다 독립적으로 정한다.
 *
 *   작년(getLastYearMonths) 중 monthlyMap 에 값이 있는 달이 하나라도 있으면 'lastYear' 모드 -
 *     base = 작년 중 데이터 있는 달들의 평균(12개월 전부가 아니라 "있는 달만", 위
 *     averageBaseMonths 규칙 그대로).
 *   작년에 전혀 데이터가 없으면(신규 지표, 도입 초기 등) 'recent3' 모드로 떨어진다 -
 *     base = 지난달로 끝나는 3개월(getBaseMonths) 중 데이터 있는 달들의 평균.
 *
 * "하나라도 있으면 작년 모드" 로 정한 이유: 작년 실적이 일부만(예: 하반기만) 있어도 그
 * 해 평균이 "최근 3개월" 보다 계절성 왜곡이 적은 더 안정적인 기준이라고 본다 - 3개월은
 * 샘플이 작아 어느 한 달의 특이값에 쉽게 흔들린다.
 */
export const chooseBaseline = (monthlyMap, today) => {
  const lastYearMonths = getLastYearMonths(today);
  const lastYearHasData = lastYearMonths.some((ym) => monthlyMap[ym] != null);
  const mode = lastYearHasData ? 'lastYear' : 'recent3';
  const baseMonths = lastYearHasData ? lastYearMonths : getBaseMonths(today);
  const { base, baseMonthCount } = averageBaseMonths(monthlyMap, baseMonths);
  return { mode, baseMonths, base, baseMonthCount };
};

/**
 * "현재 ÷ 기준 × 100" 형태 게이지(p·q·c 공통). base 가 0 이면 몇 배가 됐는지 자체가
 * 의미 없어(분모 0) null 을 낸다 - 호출부가 baseZero 플래그로 그 사유를 화면에 알려준다
 * (예: q 는 "기준 불량률 0%"). current 나 base 가 아예 없을 때도 null.
 */
const ratioGauge = (current, base) => {
  if (current == null || base == null) return null;
  if (base === 0) return null;
  return (current / base) * 100;
};

/**
 * "(기준 − 현재) ÷ 기준 × 100" 형태 게이지(납기 단축률 전용). 기준보다 짧아지면(개선)
 * 양수가 나오도록 뺄셈 방향을 고정한다 - ratioGauge 와 분자 모양이 달라 별도로 둔다.
 */
const shortenGauge = (current, base) => {
  if (current == null || base == null) return null;
  if (base === 0) return null;
  return ((base - current) / base) * 100;
};

/**
 * "(현재 − 기준) ÷ 기준 × 100" - 상단 KPI 카드 5개가 공통으로 쓰는 "변화율(pct)" 이다.
 * 게이지의 ratioGauge/shortenGauge 와 달리 좋다/나쁘다 방향을 안 따진다(단순 증감률) -
 * 카드 쪽에서 higherIsBetter 로 색을 따로 정한다(MainDashboardPage 참고). base 가 0 이면
 * 나눌 수 없으므로(0에서 몇 % 늘었는지는 정의 불가) null - baseZero 로 그 사유를 알린다.
 */
export const calcPctChange = (current, base) => {
  if (current == null || base == null || base === 0) return null;
  return ((current - base) / base) * 100;
};

/**
 * base(chooseBaseline 이 항목별로 자동 선택)와 current(이번달) 를 비교하는 계산의 공통
 * 뼈대. gaugeFn 을 주면 그 식으로 낸 "gauge" 값도 같이 돌려준다(반원 게이지용) - 안 주면
 * (카드 5개는 gaugeFn 없이 쓴다) pct(calcPctChange, 방향 안 따지는 단순 증감률)만 쓰면 된다.
 * baseMode('lastYear'|'recent3')를 결과에 같이 담아 화면이 sub-line 라벨("작년 월평균" vs
 * "3개월 월평균")을 고를 수 있게 한다.
 */
const calcTrend = (monthlyMap, today, gaugeFn) => {
  const currentYm = getCurrentYm(today);
  const currentEntry = monthlyMap[currentYm] || null;
  const { mode, baseMonths, base, baseMonthCount } = chooseBaseline(monthlyMap, today);
  const current = currentEntry ? currentEntry.value : null;
  const currentCount = currentEntry ? currentEntry.count : null;
  const result = {
    current,
    currentCount,
    base,
    baseMonthCount,
    baseMonths,
    baseMode: mode,
    currentYm,
    baseZero: base === 0,
    pct: calcPctChange(current, base),
  };
  if (gaugeFn) {
    result.gauge = gaugeFn(current, base);
  }
  return result;
};

/**
 * P: 시간당 생산량. 월별 값 = Σ생산수량 ÷ Σ작업시간 (분모 0인 달은 데이터 없음).
 * 게이지 = 이번달 ÷ base 평균 × 100. 높을수록 좋다(higherIsBetter 는 컴포넌트 쪽 설정값).
 * 상단 KPI 카드 "시간당 생산량" 도 이 함수 결과를 그대로 쓴다(같은 월별 값 정의 - Phase 3).
 * base 는 chooseBaseline 이 항목(이 productionList) 기준으로 자동 선택한다(Phase 4).
 */
export const calcThroughputTrend = (productionList, today) => {
  const monthlyMap = buildMonthlyRatio(productionList, {
    ymField: 'kpiYm', numField: 'producedQty', denField: 'workHours',
  });
  return calcTrend(monthlyMap, today, ratioGauge);
};

/**
 * Q: 완제품 불량률. 월별 값 = Σ불합격건수 ÷ Σ판정건수 × 100 (판정건수 0인 달은 데이터 없음).
 * 게이지 = 이번달 ÷ base 평균 × 100. 낮을수록 좋다. 기준(base)이 0% 면 "0에서 몇 배
 * 늘었나" 자체가 정의되지 않아 ratioGauge 가 null 을 내고, baseZero 로 그 사유를 알려준다.
 */
export const calcFinalDefectTrend = (qualityList, today) => {
  const monthlyMap = buildMonthlyRatio(qualityList, {
    ymField: 'kpiYm', numField: 'finalFailCnt', denField: 'finalInspectionCnt', multiplier: 100,
  });
  return calcTrend(monthlyMap, today, ratioGauge);
};

/**
 * C: 재공재고 (asOfDt 스냅샷 기준, Phase 2 - selectWipSnapshot). 호출부(usePqcdData)가
 * "작년 12개월 말일" + "최근 3개월 말일"(어느 모드가 될지 미리 모르니 둘 다) + "오늘"
 * 시점으로 asOfDt 를 매겨 따로 조회한 뒤, 그 결과를 [{ kpiYm, wipQty, wipPlanCnt }, ...]
 * 형태 목록(달마다 스냅샷 한 건)으로 만들어 넘긴다. 여기서는 그걸 buildMonthlyDirect 로
 * ym → 값 맵으로 바꾸고, chooseBaseline 이 그중 실제로 쓸 달들을 고른다.
 *
 * ⚠ base 평균에서 "뺄지 말지"의 기준이 다른 지표와 다르다. p/q/d 는 분모(작업시간·판정
 * 건수 등)가 0 이거나 행이 없으면 "그 달 실적 자체가 없다" 는 뜻이라 뺐지만, 재공재고는
 * "그 시점에 재공이 하나도 없다(wipQty=0)"는 게 실제로 있을 수 있는 정상 값이다 - 다 만들어서
 * 다 내보낸 시점이면 0이 맞는 값이지, "데이터가 없다"가 아니다. 그래서 0은 절대 빼면 안 되고,
 * 호출부(usePqcdData)가 스냅샷 요청 자체에 실패했을 때, 또는 existPlanCnt(그 시점에 존재한
 * 계획 수, selectWipSnapshot)가 0 일 때만(그 시점엔 아직 시스템을 안 쓰고 있었다는 뜻이라
 * "재공 0"이 아니라 "잴 대상이 없었다") wipQty 를 null 로 넣어서 그 달을 평균에서 뺀다.
 * buildMonthlyDirect 가 "값이 null 인 행만 제외"하므로 이 규칙이 자연히 지켜진다 -
 * existPlanCnt 판정 자체는 이 함수가 아니라 호출부가 한다(이 함수는 이미 null/값 으로
 * 정리된 목록을 받는다는 전제).
 * 게이지 = 이번달 ÷ base 평균 × 100. 낮을수록 좋다.
 */
export const calcWipTrend = (wipSnapshotList, today) => {
  const monthlyMap = buildMonthlyDirect(wipSnapshotList, {
    ymField: 'kpiYm', valField: 'wipQty', countField: 'wipPlanCnt',
  });
  return calcTrend(monthlyMap, today, ratioGauge);
};

/**
 * D: 납기 단축률. 월별 값은 서버(selectContractLeadKpi)가 이미 계약완료일→출하완료일
 * 평균일수(avgContractLeadDays)로 낸 것을 그대로 쓴다 - p/q/c 처럼 여기서 Σnum÷Σden 을
 * 다시 계산하지 않는다(제품 그룹핑 없이 월 하나에 행 하나라 합칠 필요가 없다).
 * "출하완료" 는 Phase 2(2026-09)부터 "납품창고 입고일" 기준이다(레거시 직송 건만 고객
 * 출하일로 대체) - selectContractLeadKpi 주석 참고.
 * 게이지 = (기준 − 이번달) ÷ 기준 × 100 - 기준보다 짧아졌으면(개선) 양수가 된다.
 */
export const calcDeliveryShortenRate = (contractLeadList, today) => {
  const monthlyMap = buildMonthlyDirect(contractLeadList, {
    ymField: 'kpiYm', valField: 'avgContractLeadDays', countField: 'totalCnt',
  });
  return calcTrend(monthlyMap, today, shortenGauge);
};

// ═══════════════════════════════════════════════════════════════════════════
// 상단 KPI 카드 5개 (Phase 3, 2026-09) - "값 = 이번달, 비교 = base(항목별 자동 선택)" 공통
// 구조(Phase 4, 2026-10 부터 base 선택이 셀렉터가 아니라 자동이다). 게이지(p/q/c/d)와 같은
// calcTrend 뼈대를 쓰지만 gaugeFn 없이 pct(calcPctChange)만 쓴다 - 카드는 반원 게이지
// 그래픽이 없어 "몇 배" 값(ratioGauge)이 필요 없고, 단순 증감률(pct)과 화살표/색만 있으면
// 된다(색·화살표는 higherIsBetter 를 아는 MainDashboardPage 가 정한다).
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 카드 "생산완료수량". 월별 값 = Σ생산수량(그 달 행 전체 합, production 목록) - 행 자체가
 * 없는 달만 데이터 없음으로 뺀다(합계가 0인 것과 "행이 없다"는 다르다 - buildMonthlySum
 * 참고). 높을수록 좋다.
 */
export const calcProducedQtyCard = (productionList, today) => {
  const monthlyMap = buildMonthlySum(productionList, { ymField: 'kpiYm', valField: 'producedQty' });
  return calcTrend(monthlyMap, today);
};

/**
 * 카드 "제조납기준수율(OTD)". 월별 값 = Σ정시납품건수 ÷ Σ총납품건수 × 100 (delivery 목록,
 * 분모 0인 달은 데이터 없음). 높을수록 좋다.
 */
export const calcOtdCard = (deliveryList, today) => {
  const monthlyMap = buildMonthlyRatio(deliveryList, {
    ymField: 'kpiYm', numField: 'onTimeCnt', denField: 'totalCnt', multiplier: 100,
  });
  return calcTrend(monthlyMap, today);
};

// 카드3(설비 평균 가동률)·카드4(제조리드타임)는 Phase 3 상단 KPI 카드에서 빠지면서
// (2026-09) 전용 계산(calcOperRateCard/calcLeadTimeCard)도 함께 걷어냈다 - 두 지표는
// KPI 리포트 화면에서 계속 볼 수 있다.
//
// 카드5(시간당 생산량)는 게이지 P 와 월별 값 정의가 완전히 같다 - 별도 함수를 만들지 않고
// 화면이 calcThroughputTrend 결과를 카드·게이지 양쪽에 그대로 쓴다.

// ═══════════════════════════════════════════════════════════════════════════
// PQCD 스코어 (Phase 4, 2026-10) - 메인 대시보드 "핵심성과지표 스코어" 레이더 +
// KPI 리포트 "PQCD 종합 스코어" 레이더가 공통으로 쓰는 점수 계산.
// ═══════════════════════════════════════════════════════════════════════════

/** 점수 상한 - base 대비 아무리 좋아져도 150점에서 자른다(레이더 반지름이 무한히 안 늘어나게). */
export const SCORE_CAP = 150;

/**
 * base=100점 기준 현재 값의 점수.
 *   higherIsBetter(높을수록 좋음, 예: 생산완료수량) : 현재 ÷ base × 100.
 *   그 외(낮을수록 좋음, 예: 완제품 불량률·재공재고) : base ÷ 현재 × 100 - 분자/분모를
 *     뒤집어야 "더 좋아짐(현재가 더 작음) = 더 높은 점수"가 된다.
 *     현재가 0이면 나눌 수 없는데, "0" 은 낮을수록 좋은 지표에서는 최선의 값이라
 *     base > 0 이면 cap(150점)을 그대로 준다 - base 도 0이면(둘 다 이미 0) 변화가
 *     없으므로 기준과 동일한 100점으로 둔다.
 * current 나 base 가 없으면(null), 또는 higherIsBetter 인데 base가 0(분모 0 - 나눌 수
 * 없음)이면 null 을 낸다 - 호출부가 null 을 "0점 + 데이터 없음" 으로 표시한다(점수 0은
 * "0점짜리 실적" 과 혼동되므로, null 과 0을 호출부가 구분해 보여줄 책임이 있다).
 */
export const calcItemScore = (current, base, higherIsBetter) => {
  if (current == null || base == null) return null;
  if (higherIsBetter) {
    if (base === 0) return null;
    return Math.min(SCORE_CAP, (current / base) * 100);
  }
  if (current === 0) {
    return base > 0 ? SCORE_CAP : 100;
  }
  return Math.min(SCORE_CAP, (base / current) * 100);
};
